// Spec s8: the panel.
//
// Fields are split into "Still needed" and "Confirmed" rather than rendered as
// one flat list. An outstanding field is drawn as a label over a blank rule - a
// space waiting to be written on - and a confirmed one collapses to a single
// quiet line. That is a departure from s8's "one row per field, in config
// order": order is preserved within each section, but a field moves between
// sections as its status changes. It serves the same intent harder, since the
// actionable set is the only thing at full weight.
//
// The panel is also where a person can answer. The model's guesses are
// disposable and recomputed every pass; a human answer is not, so it lives in a
// separate override layer applied on top (see applyOverride). Session state
// still dies with the call as far as this component is concerned; saving it is
// CallView's job, on leaving.

import { useEffect, useRef, useState } from 'react';
import {
  mergedFields,
  type FieldOverride,
  type FieldState,
  type FieldStatus,
} from '../pipeline/extraction';
import { PLACEHOLDER_ESTIMATE_USD, isComplete } from '../pipeline/estimate';
import { PriceEstimate } from './PriceEstimate';
import {
  CATEGORY_LABELS,
  FIELD,
  ORGS,
  ORG_LABELS,
  isPricingConfig,
} from '../pricing/scope';
import type { ChecklistField } from '../config/types';

type Props = {
  fields: ChecklistField[];
  /** The checklist in use, so the pricing scope can be told apart. */
  config: { id: string; builtinKey?: string | null } | null;
  state: FieldState[];
  overrides: Map<string, FieldOverride>;
  onAnswer: (fieldId: string, value: string, source: FieldOverride['source']) => void;
  onClearAnswer: (fieldId: string) => void;
  inFlight: boolean;
  elapsedMs: number;
  lastError: string | null;
  sttSeconds: number;
  passes: number;
  costUsd: number;
};

const money = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

function clock(ms: number): string {
  const total = Math.floor(ms / 1000);
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

/** Fields that just became `filled`, so they can flash once and settle. */
function useNewlyFilled(entries: FieldState[]): Set<string> {
  const previous = useRef(new Map<string, FieldStatus>());
  const [fresh, setFresh] = useState<Set<string>>(new Set());

  useEffect(() => {
    const settled = new Set<string>();
    for (const entry of entries) {
      if (entry.status === 'filled' && previous.current.get(entry.id) !== 'filled') {
        settled.add(entry.id);
      }
      previous.current.set(entry.id, entry.status);
    }
    if (settled.size > 0) {
      setFresh(settled);
      const timer = setTimeout(() => setFresh(new Set()), 600);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [entries]);

  return fresh;
}

/** Model codes are not words. "-1" and "RBG" need saying properly on screen. */
function labelFor(field: { id: string }, value: string): string {
  if (field.id === FIELD.org) return ORG_LABELS[value] ?? value;
  if (field.id === FIELD.category) return CATEGORY_LABELS[value] ?? value;
  return value;
}

function Editor({
  field,
  initial,
  onSave,
  onCancel,
}: {
  field: ChecklistField;
  initial: string;
  onSave: (value: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(initial);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  // A closed set of answers gets a list, not a text box. Typing "Water
  // Mitigation" exactly, mid-call, from a choice of fourteen, is not a thing
  // anyone should be asked to do - and a near miss is an unknown level, which
  // the model scores as though the answer were never given.
  if (field.type === 'enum' || field.type === 'boolean') {
    const options =
      field.type === 'boolean'
        ? [
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
          ]
        : (field.enumValues ?? []).map((v) => ({ value: v, label: labelFor(field, v) }));
    return (
      <div className="answer">
        <select
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') onCancel();
          }}
        >
          <option value="">Not established</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <button type="button" className="answer__save" onClick={() => onSave(value.trim())}>
          Save
        </button>
        <button type="button" className="answer__cancel" onClick={onCancel}>
          Cancel
        </button>
      </div>
    );
  }

  return (
    <div className="answer">
      <input
        ref={inputRef}
        inputMode={field.type === 'number' ? 'numeric' : undefined}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          // Mid-call, reaching for the mouse to save is friction. Enter commits.
          if (e.key === 'Enter') onSave(value.trim());
          if (e.key === 'Escape') onCancel();
        }}
      />
      <button type="button" className="answer__save" onClick={() => onSave(value.trim())}>
        Save
      </button>
      <button type="button" className="answer__cancel" onClick={onCancel}>
        Cancel
      </button>
    </div>
  );
}

export function ChecklistPanel({
  fields,
  config,
  state,
  overrides,
  onAnswer,
  onClearAnswer,
  inFlight,
  elapsedMs,
  lastError,
  sttSeconds,
  passes,
  costUsd,
}: Props) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);

  const entries = mergedFields(fields, state, overrides);
  const byId = new Map(entries.map((entry) => [entry.id, entry]));
  /** Field id -> whatever the panel currently shows. What the price is built from. */
  const answers = Object.fromEntries(entries.map((e) => [e.id, e.value]));
  const fresh = useNewlyFilled(entries);

  const pricing = isPricingConfig(config);

  // The company is the one answer a call will not give us, and the estimate
  // cannot be computed without it - so it sits at the top as a permanent
  // control rather than queueing in "still needed" like something the
  // conversation might yet resolve.
  const orgValue = pricing ? (byId.get(FIELD.org)?.value ?? '') : '';

  const listed = fields.filter((field) => !(pricing && field.id === FIELD.org));
  const open = listed.filter((field) => byId.get(field.id)?.status !== 'filled');
  const done = listed.filter((field) => byId.get(field.id)?.status === 'filled');

  const answer = (fieldId: string, value: string, source: FieldOverride['source']) => {
    setEditing(null);
    if (value) onAnswer(fieldId, value, source);
  };

  return (
    <aside className="panel">
      <div className="panelhead">
        <span className="clock">{clock(elapsedMs)}</span>
        <span className={`pass${inFlight ? ' run' : ''}`}>checking</span>
      </div>

      {pricing && (
        <div className="orgpick">
          <label htmlFor="orgpick">Company</label>
          <select
            id="orgpick"
            value={orgValue}
            onChange={(e) => {
              const v = e.target.value;
              if (v) onAnswer(FIELD.org, v, 'typed');
              else onClearAnswer(FIELD.org);
            }}
          >
            <option value="">Choose a company&hellip;</option>
            {ORGS.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
          {!orgValue && <span className="orgpick__hint">Needed before there is a price.</span>}
        </div>
      )}

      <div className="section">
        <div className="sectionhead">
          <span className="eyebrow">Still needed</span>
          <span className="count">{open.length}</span>
        </div>

        {fields.length === 0 ? (
          <p className="empty-note">No fields configured. Add some under Edit checklist.</p>
        ) : open.length === 0 ? (
          <p className="empty-note">Everything&rsquo;s covered.</p>
        ) : (
          open.map((field) => {
            const entry = byId.get(field.id)!;

            if (editing === field.id) {
              return (
                <div className="open" key={field.id}>
                  <div className="lbl">{field.label}</div>
                  <Editor
                    field={field}
                    initial={entry.value ?? ''}
                    onSave={(value) => answer(field.id, value, 'typed')}
                    onCancel={() => setEditing(null)}
                  />
                </div>
              );
            }

            if (entry.status === 'uncertain') {
              return (
                <div className="open maybe" key={field.id}>
                  <div className="lbl">{field.label}</div>
                  <div className="cand">
                    {entry.value ? (
                      <span className="val">{entry.value}</span>
                    ) : (
                      <span className="val none">mentioned, not pinned down</span>
                    )}
                  </div>
                  {/* "CONFIRM" used to be a label, which read as an instruction
                      nobody could act on. It is a button now. */}
                  <div className="rowacts">
                    {entry.value && (
                      <button
                        type="button"
                        className="rowact rowact--yes"
                        onClick={() => answer(field.id, entry.value!, 'confirmed')}
                      >
                        Confirm
                      </button>
                    )}
                    <button type="button" className="rowact" onClick={() => setEditing(field.id)}>
                      {entry.value ? 'Fix' : 'Type it in'}
                    </button>
                  </div>
                </div>
              );
            }

            return (
              <div className="open" key={field.id}>
                <div className="lbl">{field.label}</div>
                <div className="rule" />
                <div className="rowacts">
                  <button type="button" className="rowact" onClick={() => setEditing(field.id)}>
                    Type it in
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {done.length > 0 && (
        <div className="section done">
          <div className="sectionhead">
            <span className="eyebrow">Confirmed</span>
            <span className="count">{done.length}</span>
          </div>

          {done.map((field) => {
            const entry = byId.get(field.id)!;
            const isOpen = expanded === field.id;
            return (
              <div key={field.id}>
                <button
                  type="button"
                  className={`row${fresh.has(field.id) ? ' settled' : ''}`}
                  aria-expanded={isOpen}
                  onClick={() => setExpanded(isOpen ? null : field.id)}
                >
                  <span className="tick">&#9679;</span>
                  <span className="rlbl">{field.label}</span>
                  <span className="rval">{entry.value}</span>
                  <span className="chev">&#9654;</span>
                </button>

                {isOpen && (
                  <div className="evidence">
                    {editing === field.id ? (
                      <Editor
                        field={field}
                        initial={entry.value ?? ''}
                        onSave={(value) => answer(field.id, value, 'typed')}
                        onCancel={() => setEditing(null)}
                      />
                    ) : (
                      <>
                        {entry.fromUser ? (
                          <span className="q">You entered this.</span>
                        ) : entry.evidence ? (
                          <span className="q">&ldquo;{entry.evidence}&rdquo;</span>
                        ) : (
                          <span className="q">no supporting span</span>
                        )}
                        <span className="meta">
                          {entry.fromUser
                            ? 'Kept as you set it; later passes will not overwrite it.'
                            : `${Math.round(entry.confidence * 100)}% confidence`}
                          {!entry.fromUser && entry.evidence && !entry.evidenceVerified && (
                            <span className="warn"> &middot; span not found verbatim</span>
                          )}
                        </span>
                        <div className="rowacts">
                          <button
                            type="button"
                            className="rowact"
                            onClick={() => setEditing(field.id)}
                          >
                            Fix
                          </button>
                          {entry.fromUser && (
                            <button
                              type="button"
                              className="rowact"
                              onClick={() => onClearAnswer(field.id)}
                            >
                              Let the model decide
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* The payoff for finishing the checklist. Hidden until every field is
          answered, and never shown for an empty checklist - zero fields
          outstanding out of zero is not a completed inspection. */}
      {pricing ? (
        <PriceEstimate answers={answers} />
      ) : (
        isComplete(entries) && (
          <div className="estimate">
            <span className="estimate__label">Estimated project price</span>
            <span className="estimate__value">{money.format(PLACEHOLDER_ESTIMATE_USD)}</span>
            <span className="estimate__note">
              Placeholder figure &mdash; not a real estimate yet.
            </span>
          </div>
        )
      )}

      {lastError && (
        <p className="panelerr" role="status">
          Last pass failed &mdash; showing the previous result. Retrying.
        </p>
      )}

      {/* Spec s11: cost visible before call volume grows, without competing
          with the checklist for attention. */}
      <div className="meter">
        <span>
          audio <b>{clock(sttSeconds * 1000)}</b>
        </span>
        <span>
          passes <b>{passes}</b>
        </span>
        <span>
          cost <b>${costUsd.toFixed(4)}</b>
        </span>
      </div>
    </aside>
  );
}
